import { useEffect, useCallback, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import './WebsitePersonalization.css';

interface WebsitePersonalizationProps {
  apiBaseUrl: string;
}

// Each description is a sequence of segments. `mark: true` segments render
// inside <mark>, the rest render as plain text. Sequencing copy this way lets
// the typewriter loop slice character-by-character through the segment list
// while still applying the highlight style at exactly the right offsets.
interface CopySegment {
  text: string;
  mark?: boolean;
}

interface Persona {
  name: string;       // first name, used in "Personalized for X" label
  fullName: string;   // shown next to avatar
  initial: string;    // single letter in the avatar circle
  accent: string;
  tags: string[];     // 3 short interest chips, drives the personalization
  copy: CopySegment[];
}

const PERSONAS: Persona[] = [
  {
    name: 'Sarah',
    fullName: 'Sarah Mitchell',
    initial: 'S',
    accent: '#c89968',
    tags: ['Loves linen', 'Warm tones', 'Weekend wear'],
    copy: [
      { text: 'Crafted from ' },
      { text: '100% pure European linen', mark: true },
      { text: ', with a relaxed drape that\u2019s ' },
      { text: 'Sarah-approved', mark: true },
      { text: ' for breezy weekends.' },
    ],
  },
  {
    name: 'Mike',
    fullName: 'Mike Chen',
    initial: 'M',
    accent: '#5ea7c8',
    tags: ['Runs hot', 'Hates returns', 'Active lifestyle'],
    copy: [
      { text: 'A ' },
      { text: 'naturally breathable', mark: true },
      { text: ' weave keeps you cool, with ' },
      { text: 'temperature-regulating fibers', mark: true },
      { text: ' built for warm climates.' },
    ],
  },
  {
    name: 'Priya',
    fullName: 'Priya Shah',
    initial: 'P',
    accent: '#a89280',
    tags: ['Likes neutrals', 'Capsule wardrobe', 'Minimalist'],
    copy: [
      { text: 'A ' },
      { text: 'timeless neutral palette', mark: true },
      { text: ' in three earth tones, ' },
      { text: 'pairing effortlessly', mark: true },
      { text: ' with everything in your wardrobe.' },
    ],
  },
];

const TYPE_SPEED_MS = 32;
const PERSONA_HOLD_AFTER_TYPING_MS = 1700;
const PERSONA_SWAP_PAUSE_MS = 380;

function totalChars(copy: CopySegment[]): number {
  return copy.reduce((sum, s) => sum + s.text.length, 0);
}

// Render a partial typewriter slice of a segmented copy block. We walk through
// each segment in order, taking only as many characters as remain in the
// global typed budget. Once the budget runs out the remaining segments still
// render as empty placeholders so the layout doesn't reflow when the cursor
// crosses a <mark> boundary.
function renderTypedCopy(copy: CopySegment[], typed: number) {
  let remaining = typed;
  return copy.map((seg, i) => {
    const visible = seg.text.slice(0, Math.max(0, remaining));
    remaining -= seg.text.length;
    const finishedTypingThisSegment = visible.length === seg.text.length;
    if (seg.mark) {
      return (
        <mark
          key={i}
          className={finishedTypingThisSegment ? 'wp-mark wp-mark-done' : 'wp-mark'}
        >
          {visible}
        </mark>
      );
    }
    return <span key={i}>{visible}</span>;
  });
}

export function WebsitePersonalization(_props: WebsitePersonalizationProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [show, setShow] = useState(false);
  const [persona, setPersona] = useState(0);
  const [charsTyped, setCharsTyped] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      // Reset state for a fresh cycle.
      setShow(false);
      setPersona(0);
      setCharsTyped(0);
      setIsTyping(false);
      setFadeOut(false);
      await sleep(380);
      if (!loopRef.current) return;

      // Entrance: persona card + product card slide in.
      setShow(true);
      await sleep(620);
      if (!loopRef.current) return;

      for (let pIdx = 0; pIdx < PERSONAS.length; pIdx++) {
        if (!loopRef.current) return;
        // Switch persona card (avatar + tags morph). Description clears so
        // we can typewriter the new copy onto a blank line.
        setPersona(pIdx);
        setCharsTyped(0);
        setIsTyping(false);
        await sleep(PERSONA_SWAP_PAUSE_MS);
        if (!loopRef.current) return;

        // Type the new description char-by-char. The blinking cursor is
        // rendered while isTyping is true.
        setIsTyping(true);
        const total = totalChars(PERSONAS[pIdx].copy);
        for (let c = 1; c <= total; c++) {
          if (!loopRef.current) return;
          setCharsTyped(c);
          await sleep(TYPE_SPEED_MS);
        }
        setIsTyping(false);

        // Settle pause so the viewer can read the finished, highlighted copy
        // before we move on to the next persona.
        await sleep(PERSONA_HOLD_AFTER_TYPING_MS);
      }

      // Fade out everything before the loop restart pause.
      setFadeOut(true);
      await sleep(580);
      if (!loopRef.current) return;
      await sleep(420);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  const active = PERSONAS[persona];
  const stageStyle = {
    '--wp-accent': active.accent,
    '--wp-accent-local': active.accent,
  } as CSSProperties;

  return (
    <div className="website-personalization">
      <div
        className={`wp-stage${show ? ' wp-show' : ''}${fadeOut ? ' wp-fade-out' : ''}`}
        style={stageStyle}
      >
        {/* Persona card -- the prominent "who is viewing this page" indicator.
            Avatar, name, and three interest chips morph together when persona
            changes; the rest of the layout stays put so the change reads as
            "same page, different shopper". */}
        <div className="wp-persona-card sc-glass-card">
          <div
            className="wp-persona-avatar"
            style={{ background: active.accent } as CSSProperties}
            aria-hidden="true"
          >
            {active.initial}
          </div>
          <div className="wp-persona-info">
            <div className="wp-persona-name">{active.fullName}</div>
            <div className="wp-persona-tags">
              {active.tags.map((tag) => (
                <span key={tag} className="wp-persona-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Static product card -- same product across all three personas, so
            the viewer sees that the description (not the product) is what
            gets rewritten. */}
        <div className="sc-glass-card wp-card">
          <div className="wp-blob" aria-hidden="true">
            <div className="wp-blob-weave" />
            <div className="wp-blob-shadow" />
          </div>
          <div className="wp-info">
            <div className="wp-name">The Heritage Linen Shirt</div>
            <div className="wp-price">$148</div>
          </div>
        </div>

        <div className="wp-desc-wrap">
          <div className="sc-section-label wp-desc-label">
            <span className="wp-desc-dot" aria-hidden="true" />
            Personalized for{' '}
            <strong className="wp-desc-name">{active.name}</strong>
          </div>
          <div className="sc-glass-subtle wp-desc-card">
            <p className="wp-desc">
              {renderTypedCopy(active.copy, charsTyped)}
              {isTyping && (
                <span className="wp-cursor" aria-hidden="true" />
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
